import { Card, Stack, Text } from '@components/ui';
import { formatMoney } from '@features/buyer/hire/formatMoney';
import { useHireJob } from '@features/buyer/hire/hire-job-context';
import type { Category } from '@features/buyer/hire/job-price-calculator/jobPriceCalculatorTypes';
import { useCategoriesQuery } from '@lib/api/categories';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';

const joinParts = (...parts: (string | null | undefined)[]): string =>
  parts.filter(Boolean).join(' ');

interface SummaryRow {
  label: string;
  value: string;
}

export const HireSummary = (): JSX.Element => {
  const { t } = useTranslation();
  const { createJob } = useHireJob();
  const { data: categoryData } = useCategoriesQuery();
  const categories = (categoryData ?? []) as Category[];
  const category = categories.find((item) => item.id === createJob.categoryId);
  const subCategory = category?.subCategories?.find(
    (item) => item.id === createJob.subCategoryId
  );
  const isAutomotive = createJob.isAutomotive !== false;
  const baseFee = formatMoney(createJob.buyerCost);
  const discountedFee = formatMoney(createJob.discountedBuyerCost);

  const rows: SummaryRow[] = [
    {
      label: t('hire.flow.category'),
      value: [category?.name, subCategory?.name].filter(Boolean).join(' · '),
    },
    {
      label: t('hire.flow.askingPrice'),
      value: formatMoney(createJob.askingPrice),
    },
    {
      label: t(isAutomotive ? 'hire.flow.vehicle' : 'hire.flow.item'),
      value:
        joinParts(createJob.year, createJob.make, createJob.model) ||
        createJob.description,
    },
    {
      label: t('hire.flow.location'),
      value: createJob.address,
    },
    {
      label: t('hire.flow.contact'),
      value: createJob.contactName,
    },
  ].filter((row) => row.value);

  return (
    <Card sx={{ p: 3 }}>
      <Text as="h2" variant="label" sx={{ fontWeight: 700, mb: 2 }}>
        {t('hire.flow.summaryTitle')}
      </Text>
      {rows.length === 0 ? (
        <Text variant="body" tone="muted" sx={{ fontSize: 14 }}>
          {t('hire.flow.summaryEmpty')}
        </Text>
      ) : (
        <Stack gap="md" component="dl" sx={{ m: 0 }}>
          {rows.map((row) => (
            <Box key={row.label}>
              <Text as="dt" variant="caption" tone="muted">
                {row.label}
              </Text>
              <Text
                as="dd"
                variant="body"
                sx={{
                  m: 0,
                  fontSize: 14,
                  fontWeight: 500,
                  wordBreak: 'break-word',
                }}
              >
                {row.value}
              </Text>
            </Box>
          ))}
        </Stack>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          mt: 2.5,
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Text variant="body" tone="muted" sx={{ fontSize: 14 }}>
          {t('hire.flow.fee')}
        </Text>
        <Box sx={{ textAlign: 'right' }}>
          {discountedFee ? (
            <Text
              as="p"
              variant="caption"
              tone="muted"
              sx={{ textDecoration: 'line-through' }}
            >
              {baseFee}
            </Text>
          ) : null}
          <Text
            as="p"
            variant="subtitle"
            sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
          >
            {discountedFee || baseFee || '—'}
          </Text>
        </Box>
      </Box>
    </Card>
  );
};
